<template>
  <div class="p-6 md:p-10">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div>
        <p class="text-xs uppercase tracking-[0.4em] text-amber-500 font-semibold">Manage</p>
        <h1 class="mt-2 text-3xl font-bold text-gray-900">{{ title }}</h1>
      </div>
      <div class="flex items-center gap-3">
        <button
          v-if="seedData && seedData.length"
          @click="handleSeed"
          :disabled="seeding"
          class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 disabled:opacity-60"
        >
          {{ seeding ? 'Importing...' : 'Import Default Data' }}
        </button>
        <button
          @click="openAddModal"
          class="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
        >
          + Add New
        </button>
      </div>
    </div>

    <p v-if="feedback" class="mb-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
      {{ feedback }}
    </p>
    <p v-if="loadError" class="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
      {{ loadError }}
    </p>

    <!-- Search -->
    <div v-if="!loading && items.length > 0" class="relative mb-6 max-w-sm">
      <svg class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="`Search ${title.toLowerCase()}...`"
        class="w-full rounded-full border border-gray-200 py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:border-amber-500"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="py-16 text-center text-gray-400">Loading...</div>

    <!-- Empty state -->
    <div v-else-if="items.length === 0" class="rounded-[24px] border border-dashed border-gray-200 py-16 text-center">
      <p class="text-gray-500">No {{ title.toLowerCase() }} yet.</p>
      <button @click="openAddModal" class="mt-4 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600">
        + Add the first one
      </button>
    </div>

    <!-- No search matches -->
    <div v-else-if="filteredItems.length === 0" class="rounded-[24px] border border-dashed border-gray-200 py-16 text-center">
      <p class="text-gray-500">No {{ title.toLowerCase() }} match "{{ searchQuery }}".</p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto rounded-[24px] border border-gray-100 bg-white shadow-sm">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <th v-if="imageField" class="px-5 py-3 font-semibold">Image</th>
            <th v-for="col in columns" :key="col.key" class="px-5 py-3 font-semibold">{{ col.label }}</th>
            <th class="px-5 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="item in filteredItems" :key="item.id">
            <td v-if="imageField" class="px-5 py-3">
              <img
                v-if="item[imageField.key]"
                :src="toDirectImageUrl(item[imageField.key])"
                alt=""
                class="h-12 w-16 rounded-lg object-cover bg-gray-100"
              />
              <div v-else class="h-12 w-16 rounded-lg bg-gray-100"></div>
            </td>
            <td v-for="col in columns" :key="col.key" class="px-5 py-3 text-gray-700 max-w-xs truncate">
              {{ formatColumnValue(item, col) }}
            </td>
            <td class="px-5 py-3 text-right space-x-3 whitespace-nowrap">
              <router-link
                v-if="printRoute"
                :to="`${printRoute}/${printRouteParam ? printRouteParam(item) : item.id}`"
                target="_blank"
                class="text-sm font-semibold text-gray-600 hover:text-gray-800"
              >Print</router-link>
              <button @click="openEditModal(item)" class="text-sm font-semibold text-amber-600 hover:text-amber-700">Edit</button>
              <button @click="confirmDelete(item)" class="text-sm font-semibold text-red-500 hover:text-red-600">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-4 py-8" @click.self="closeModal">
      <div class="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-[28px] bg-white p-5 shadow-2xl sm:p-8">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-gray-900">{{ editingId ? 'Edit' : 'Add' }} {{ singularTitle }}</h2>
          <button @click="closeModal" class="rounded-full bg-gray-100 p-2 text-gray-500 hover:bg-gray-200">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-4">
          <template v-for="field in fields" :key="field.key">
          <div v-if="!field.showIf || formData[field.showIf]">
            <label class="text-xs uppercase tracking-wide text-gray-500">{{ field.label }}</label>

            <!-- text / number / date -->
            <input
              v-if="field.type === 'text' || field.type === 'number' || field.type === 'date'"
              v-model="formData[field.key]"
              :type="field.type"
              :required="field.required"
              :min="field.type === 'date' ? minDateFor(field) : null"
              @change="field.type === 'date' ? handleDateChange(field) : null"
              class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
            />

            <!-- textarea -->
            <textarea
              v-else-if="field.type === 'textarea'"
              v-model="formData[field.key]"
              rows="3"
              :required="field.required"
              class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
            ></textarea>

            <!-- select -->
            <select
              v-else-if="field.type === 'select'"
              v-model="formData[field.key]"
              class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
            >
              <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
            </select>

            <!-- selectWithOther: dropdown + free-text "Other" fallback -->
            <div v-else-if="field.type === 'selectWithOther'">
              <select
                :value="isOtherValue(field, formData[field.key]) ? '__other__' : formData[field.key]"
                @change="handleSelectWithOtherChange(field, $event.target.value)"
                class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
              >
                <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                <option value="__other__">Other</option>
              </select>
              <input
                v-if="isOtherValue(field, formData[field.key])"
                v-model="formData[field.key]"
                type="text"
                placeholder="Type a custom value"
                class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
              />
            </div>

            <!-- boolean -->
            <label v-else-if="field.type === 'boolean'" class="mt-2 flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" v-model="formData[field.key]" class="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
              {{ field.checkboxLabel || 'Yes' }}
            </label>

            <!-- stringList (comma separated) -->
            <div v-else-if="field.type === 'stringList'">
              <input
                v-model="stringListDrafts[field.key]"
                type="text"
                placeholder="Separate items with commas"
                class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
              />
              <p class="mt-1 text-xs text-gray-400">e.g. {{ field.example || 'Item one, Item two, Item three' }}</p>
            </div>

            <!-- image (Drive URL or direct link) -->
            <div v-else-if="field.type === 'image'">
              <input
                v-model="formData[field.key]"
                type="text"
                placeholder="Paste a Google Drive share link or direct image URL"
                class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
              />
              <p class="mt-1 text-xs text-gray-400">
                From Google Drive: right-click the file &rarr; Share &rarr; "Anyone with the link" &rarr; Copy link, then paste it here.
              </p>
              <img
                v-if="formData[field.key]"
                :src="toDirectImageUrl(formData[field.key])"
                alt="Preview"
                class="mt-3 h-32 w-full rounded-2xl object-cover bg-gray-100"
              />
            </div>

            <!-- pricingRows: array of { type, price } -->
            <div v-else-if="field.type === 'pricingRows'" class="mt-2 space-y-2">
              <div v-for="(row, idx) in formData[field.key]" :key="idx" class="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  v-model="row.type"
                  type="text"
                  placeholder="Room type (e.g. Quad)"
                  class="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 sm:flex-1"
                />
                <div class="flex items-center gap-2">
                  <input
                    v-model.number="row.price"
                    type="number"
                    placeholder="Price"
                    class="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 sm:w-32"
                  />
                  <button type="button" @click="formData[field.key].splice(idx, 1)" class="flex-shrink-0 text-red-500 hover:text-red-600">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="formData[field.key].push({ type: '', price: 0 })"
                class="text-sm font-semibold text-amber-600 hover:text-amber-700"
              >
                + Add room type
              </button>
            </div>

            <!-- lineItems: array of { description, quantity, unitPrice } -->
            <div v-else-if="field.type === 'lineItems'" class="mt-2 space-y-2">
              <div v-for="(row, idx) in formData[field.key]" :key="idx" class="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  v-model="row.description"
                  type="text"
                  placeholder="Description (e.g. Umrah Package - Executive Retreat)"
                  class="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 sm:flex-1"
                />
                <div class="flex items-center gap-2">
                  <input
                    v-model.number="row.quantity"
                    type="number"
                    min="1"
                    placeholder="Qty"
                    class="w-1/2 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 sm:w-20"
                  />
                  <input
                    v-model.number="row.unitPrice"
                    type="number"
                    placeholder="Unit Price"
                    class="w-1/2 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 sm:w-32"
                  />
                  <button type="button" @click="formData[field.key].splice(idx, 1)" class="flex-shrink-0 text-red-500 hover:text-red-600">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="formData[field.key].push({ description: '', quantity: 1, unitPrice: 0 })"
                class="text-sm font-semibold text-amber-600 hover:text-amber-700"
              >
                + Add line item
              </button>
            </div>

            <!-- roomRows: array of { qty, roomType, view, meal, checkIn, checkOut, nights, hcn } -->
            <div v-else-if="field.type === 'roomRows'" class="mt-2 space-y-3">
              <div v-for="(row, idx) in formData[field.key]" :key="idx" class="rounded-xl border border-gray-200 p-3">
                <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <input v-model.number="row.qty" type="number" min="1" placeholder="Qty" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500" />
                  <select v-model="row.roomType" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500">
                    <option value="" disabled>Room Type</option>
                    <option v-for="rt in roomTypeOptions" :key="rt" :value="rt">{{ rt }}</option>
                  </select>
                  <input v-model="row.view" type="text" placeholder="View" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500" />
                  <input v-model="row.meal" type="text" placeholder="Meal" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500" />
                  <input v-model="row.checkIn" type="date" @change="updateRoomNights(row)" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500" />
                  <input v-model="row.checkOut" type="date" :min="addDays(row.checkIn, 1)" @change="updateRoomNights(row)" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500" />
                  <input :value="row.nights" type="number" placeholder="Nights (auto)" readonly class="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm text-gray-500" />
                  <input v-model="row.hcn" type="text" placeholder="HCN" class="rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <button type="button" @click="formData[field.key].splice(idx, 1)" class="mt-2 text-xs font-semibold text-red-500 hover:text-red-600">
                  Remove Room
                </button>
              </div>
              <button
                type="button"
                @click="formData[field.key].push({ qty: 1, roomType: '', view: '', meal: '', checkIn: '', checkOut: '', nights: 0, hcn: '' })"
                class="text-sm font-semibold text-amber-600 hover:text-amber-700"
              >
                + Add Room
              </button>
            </div>
          </div>
          </template>

          <p v-if="formError" class="text-sm text-red-500">{{ formError }}</p>

          <div class="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              :disabled="saving"
              class="rounded-2xl bg-amber-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600 disabled:opacity-60"
            >
              {{ saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Add') }}
            </button>
            <button type="button" @click="closeModal" class="rounded-2xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deletingItem" class="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-4" @click.self="deletingItem = null">
      <div class="w-full max-w-sm rounded-[28px] bg-white p-8 shadow-2xl text-center">
        <h3 class="text-lg font-bold text-gray-900">Delete this entry?</h3>
        <p class="mt-2 text-sm text-gray-500">This can't be undone.</p>
        <div class="mt-6 flex items-center justify-center gap-3">
          <button @click="handleDelete" class="rounded-2xl bg-red-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-600">Delete</button>
          <button @click="deletingItem = null" class="rounded-2xl border border-gray-200 px-6 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listItems, addItem, updateItem, deleteItem, seedDefaultsIfEmpty } from '../../services/dataStore'
import { toDirectImageUrl } from '../../utils/driveImage'

export default {
  name: 'AdminCrudPage',
  props: {
    collectionName: { type: String, required: true },
    title: { type: String, required: true },
    singularTitle: { type: String, required: true },
    fields: { type: Array, required: true },
    columns: { type: Array, required: true },
    seedData: { type: Array, default: () => [] },
    printRoute: { type: String, default: '' },
    // Optional (item) => string, used as the URL param appended to
    // printRoute instead of item.id -- e.g. Ledger prints per customer
    // name, not per individual entry.
    printRouteParam: { type: Function, default: null },
    // Optional async () => partialFormData, merged into the blank form
    // when "Add New" is opened -- used e.g. to pre-fill an auto-number.
    defaultValues: { type: Function, default: null },
    // Optional async (newItem) => void, called after a successful add
    // (not edit) -- used e.g. to advance an auto-numbering counter.
    onAdded: { type: Function, default: null }
  },
  data() {
    return {
      items: [],
      loading: true,
      loadError: '',
      feedback: '',
      seeding: false,
      showModal: false,
      editingId: null,
      formData: {},
      stringListDrafts: {},
      formError: '',
      saving: false,
      deletingItem: null,
      roomTypeOptions: ['Double', 'Triple', 'Quad', 'Quint', 'Hexa'],
      searchQuery: ''
    }
  },
  computed: {
    imageField() {
      return this.fields.find(f => f.type === 'image')
    },
    filteredItems() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.items
      return this.items.filter(item =>
        this.columns.some(col => {
          const value = this.formatColumnValue(item, col)
          return value != null && String(value).toLowerCase().includes(q)
        })
      )
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    toDirectImageUrl,
    isOtherValue(field, value) {
      return !(field.options || []).includes(value)
    },
    handleSelectWithOtherChange(field, selected) {
      this.formData[field.key] = selected === '__other__' ? '' : selected
    },
    // Adds `days` to an ISO 'YYYY-MM-DD' date string. Returns null for an
    // empty/invalid input so it's safe to bind straight to a date input's
    // :min -- an unset min just means "no restriction".
    addDays(dateStr, days) {
      if (!dateStr) return null
      const d = new Date(`${dateStr}T00:00:00`)
      if (Number.isNaN(d.getTime())) return null
      d.setDate(d.getDate() + days)
      return d.toISOString().slice(0, 10)
    },
    // A date field can declare minField: 'otherFieldKey' so its picker
    // disables any day on or before that other date (e.g. Check-Out must
    // be after Check-In).
    minDateFor(field) {
      if (!field.minField) return null
      return this.addDays(this.formData[field.minField], 1)
    },
    // If changing this date field leaves another field's value earlier
    // than its new minimum, clear that now-invalid value instead of
    // silently keeping a Check-Out that's before Check-In.
    handleDateChange(field) {
      this.fields.forEach(f => {
        if (f.minField !== field.key) return
        const min = this.minDateFor(f)
        if (this.formData[f.key] && min && this.formData[f.key] < min) {
          this.formData[f.key] = ''
        }
      })
    },
    updateRoomNights(row) {
      if (!row.checkIn || !row.checkOut) return
      const inD = new Date(`${row.checkIn}T00:00:00`)
      const outD = new Date(`${row.checkOut}T00:00:00`)
      const diff = Math.round((outD - inD) / 86400000)
      row.nights = diff > 0 ? diff : 0
      if (row.checkOut <= row.checkIn) row.checkOut = ''
    },
    formatColumnValue(item, col) {
      if (col.compute) return col.compute(item)
      const value = item[col.key]
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      if (col.key.toLowerCase().includes('price') && typeof value === 'number') {
        return `Rs ${value.toLocaleString('en-PK')}`
      }
      return value
    },
    async loadItems() {
      this.loading = true
      this.loadError = ''
      try {
        this.items = await listItems(this.collectionName, this.seedData)
      } catch (e) {
        this.loadError = 'Could not load data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    blankFormData() {
      const data = {}
      const drafts = {}
      this.fields.forEach(field => {
        if (field.type === 'stringList') {
          data[field.key] = []
          drafts[field.key] = ''
        } else if (field.type === 'pricingRows' || field.type === 'lineItems' || field.type === 'roomRows') {
          data[field.key] = []
        } else if (field.type === 'boolean') {
          data[field.key] = false
        } else if (field.type === 'number') {
          data[field.key] = field.default ?? 0
        } else if (field.type === 'select' || field.type === 'selectWithOther') {
          data[field.key] = field.options?.[0] || ''
        } else {
          data[field.key] = field.default ?? ''
        }
      })
      return { data, drafts }
    },
    async openAddModal() {
      const { data, drafts } = this.blankFormData()
      if (this.defaultValues) {
        Object.assign(data, await this.defaultValues())
      }
      this.formData = data
      this.stringListDrafts = drafts
      this.editingId = null
      this.formError = ''
      this.showModal = true
    },
    openEditModal(item) {
      const { data, drafts } = this.blankFormData()
      this.fields.forEach(field => {
        if (field.key in item) {
          data[field.key] = (field.type === 'pricingRows' || field.type === 'lineItems' || field.type === 'roomRows')
            ? (item[field.key] || []).map(row => ({ ...row }))
            : item[field.key]
        }
        if (field.type === 'stringList') {
          drafts[field.key] = (item[field.key] || []).join(', ')
        }
      })
      this.formData = data
      this.stringListDrafts = drafts
      this.editingId = item.id
      this.formError = ''
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    buildPayload() {
      const payload = { ...this.formData }
      this.fields.forEach(field => {
        if (field.type === 'stringList') {
          payload[field.key] = (this.stringListDrafts[field.key] || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        }
        if (field.type === 'number') {
          payload[field.key] = Number(payload[field.key]) || 0
        }
      })
      return payload
    },
    async handleSubmit() {
      this.formError = ''
      this.saving = true
      try {
        const payload = this.buildPayload()
        if (this.editingId) {
          await updateItem(this.collectionName, this.editingId, payload)
          this.showFeedback('Changes saved.')
        } else {
          const newItem = await addItem(this.collectionName, payload)
          if (this.onAdded) await this.onAdded(newItem)
          this.showFeedback('Added successfully.')
        }
        this.showModal = false
        await this.loadItems()
      } catch (e) {
        this.formError = `Could not save: ${e?.message || e}`
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      this.deletingItem = item
    },
    async handleDelete() {
      if (!this.deletingItem) return
      try {
        await deleteItem(this.collectionName, this.deletingItem.id)
        this.deletingItem = null
        this.showFeedback('Deleted.')
        await this.loadItems()
      } catch (e) {
        this.deletingItem = null
        this.loadError = 'Could not delete. Please try again.'
      }
    },
    async handleSeed() {
      this.seeding = true
      try {
        const count = await seedDefaultsIfEmpty(this.collectionName, this.seedData)
        this.showFeedback(count > 0 ? `Imported ${count} default entries.` : 'Already has data — nothing imported.')
        await this.loadItems()
      } catch (e) {
        this.loadError = 'Could not import default data.'
      } finally {
        this.seeding = false
      }
    },
    showFeedback(message) {
      this.feedback = message
      setTimeout(() => {
        if (this.feedback === message) this.feedback = ''
      }, 3000)
    }
  }
}
</script>
